import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { newProductConstant } from '../../../constants/product/newProduct.constant';
import { useGetNewProductQuery } from '../../../hooks/api/product/queries/useGetNewProduct.query';
import { useGetProductsQuery } from '../../../hooks/api/product/queries/useGetProducts.query';
import { useAddFilterOptions } from '../../../hooks/filter/useAddFilterOptions';
import { useChangeFilterOptionIsSelectedBySearchParam } from '../../../hooks/filter/useChangeFilterOptionIsSelectedBySearchParam';
import { useChangeSearchParamBySelectedFilterOption } from '../../../hooks/filter/useChangeSearchParamBySelectedFilterOption';
import { useMarkNotificationAsRead } from '../../../hooks/page/useMarkNotificationAsRead';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { productFilterSlice } from '../../../store/productFilterSlice';
import { createProductFilterSearchParams } from '../../../utils/filter/createProductFilterSearchParams.util';
import { ProductVerticalListDetailView } from '../../../views/Product/listView/ProductVerticalListDetailView/ProductVerticalListDetailView';
import { ProductVerticalListDetailViewGroup } from '../../../views/Product/listView/ProductVerticalListDetailView/ProductVerticalListDetailViewGroup';
import { EmptyList } from './components/EmptyList';
import { EmptySpace } from './components/EmptySpace';
import { MarketAlarmButton } from './components/MarketAlarmButton';
import { ProductCategoryDescription } from './components/ProductCategoryDescription';
import { ProductFilterSection } from './components/ProductFilterSection/ProductFilterSection';
import { ProductListPageWrapper } from './components/ProductListPageWrapper';
import { SellTractorButton } from '../../OpenMarket/OpenMarketListPage/components/SellTractorButton';

export const ProductListPage = () => {
  useAddFilterOptions({ data: 'brands' });
  const dispatch = useAppDispatch();
  useChangeFilterOptionIsSelectedBySearchParam();
  useChangeSearchParamBySelectedFilterOption();
  const { userProfile } = useGetUserProfile();
  const [searchParams] = useSearchParams();
  const productFilterActions = productFilterSlice.actions;

  const productFilterStates = useAppSelector((state) => state.productFilter);

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: false }} headerTitle={'인증트랙터'} hasMyInfoButton />
        <ProductFilterSection />
        <MarketAlarmButton pageType={'PLP'} />
      </>
    ),
    fixedHeaderHeight: userProfile?.advertisementTermsAgreement ? 102 : 138,
    fixedFooter: <SellTractorButton pageType={'plp'} />,
    fixedFooterHeight: 0,
  });

  const { data: products, isLoading: isGetProductsQueryLoading } = useGetProductsQuery({
    searchParam: createProductFilterSearchParams({ filters: productFilterStates.globalProductFilters }),
  });

  const { data: newProduct } = useGetNewProductQuery({
    searchParam: { category: newProductConstant.market.category },
  });
  useMarkNotificationAsRead(newProduct, newProductConstant.market.key);

  useEffect(() => {
    if (searchParams.size > 0) {
      dispatch(productFilterActions.changeIsSearchParamChecked(false));
    }
  }, []);

  useEffect(() => {
    // 매물이 바뀔 때 마다(= 검색 결과가 바뀔 때 마다) 경우 PLP의 Scroll Y 값을 0 으로 변경
    if (products) {
      window.scrollTo(0, 0);
    }
  }, [products]);

  return (
    <>
      <Helmet>
        <title>{pageInfo.productListPage.title}</title>
      </Helmet>

      <ProductListPageWrapper>
        <EmptySpace />

        <ProductCategoryDescription />

        {isGetProductsQueryLoading && <AnimateLoading />}
        {!isGetProductsQueryLoading && products && products.length === 0 && <EmptyList />}
        {!isGetProductsQueryLoading && products && products.length > 0 && (
          <ProductVerticalListDetailViewGroup>
            {products.map((product) => (
              <ProductVerticalListDetailView
                key={product.id}
                product={product}
                category={product.category === 'PLUVA' ? 'PLUVA' : 'PARTNER'}
              />
            ))}
          </ProductVerticalListDetailViewGroup>
        )}
      </ProductListPageWrapper>
    </>
  );
};
